import { useEffect, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Compound date field: manual typing (dd/mm/yyyy, dd-mm-yyyy, ddmmyyyy) + calendar popover.
 *
 * UX rules:
 *  - Free-form typing while focused. NO auto-insert slash saat ngetik (user bisa
 *    hapus/edit natural). Parsing dilakukan HANYA saat blur / Enter.
 *  - Klik ikon kalender → popup terbuka. Klik hari → popup auto-close & field ter-update.
 *  - Popup tidak menutup kalau user tetap ngetik di text field (interact di dalam
 *    trigger group). Popup menutup kalau klik di luar (default Radix behavior).
 *
 * value → "yyyy-mm-dd" (ISO) or ""
 * onChange(iso) → "yyyy-mm-dd" or ""
 */
export default function DateField({
  value,
  onChange,
  placeholder = "dd/mm/yyyy",
  disabled = false,
  className = "",
  testid,
  fromYear,
  toYear,
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(isoToDMY(value));
  const skipSyncRef = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (skipSyncRef.current) { skipSyncRef.current = false; return; }
    setText(isoToDMY(value));
  }, [value]);

  const parsed = value ? new Date(`${value}T00:00:00`) : undefined;

  const nowY = new Date().getFullYear();
  const yFrom = fromYear ?? nowY - 60;
  const yTo = toYear ?? nowY + 5;

  const commit = (raw) => {
    if ((raw ?? "").trim() === "") {
      skipSyncRef.current = true;
      onChange("");
      setText("");
      return;
    }
    const iso = dmyToISO(raw);
    if (iso) {
      skipSyncRef.current = true;
      onChange(iso);
      setText(isoToDMY(iso));
    } else {
      // Invalid → revert ke ISO parent terakhir.
      setText(isoToDMY(value));
    }
  };

  const handleCalendarSelect = (d) => {
    if (!d) { onChange(""); setText(""); setOpen(false); return; }
    const pad = (n) => String(n).padStart(2, "0");
    const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    skipSyncRef.current = true;
    onChange(iso);
    setText(isoToDMY(iso));
    setOpen(false);
  };

  return (
    <div className={cn("relative w-full", className)}>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        data-testid={testid}
        disabled={disabled}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(e.currentTarget.value); }
          else if (e.key === "Escape" && open) { e.preventDefault(); setOpen(false); }
        }}
        placeholder={placeholder}
        className="w-full h-9 border border-slate-300 bg-white pl-3 pr-9 text-xs tabular-nums outline-none focus:border-[#0B1D3A] disabled:opacity-50 rounded-none"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid ? `${testid}-btn` : undefined}
            disabled={disabled}
            aria-label="Buka kalender"
            onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-100 disabled:opacity-50"
          >
            <CalendarDays size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-auto p-0 rounded-none bg-white z-50"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            // Jangan close popup kalau user klik text field-nya sendiri (mereka
            // mungkin lagi ngetik manual sekaligus melihat calendar).
            const target = e.target;
            if (target && inputRef.current && inputRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
        >
          <Calendar
            mode="single"
            selected={parsed}
            onSelect={handleCalendarSelect}
            defaultMonth={parsed || new Date()}
            captionLayout="dropdown-buttons"
            fromYear={yFrom}
            toYear={yTo}
          />
          {value && (
            <div className="border-t border-slate-200 p-2 flex justify-end">
              <button
                type="button"
                onClick={() => { onChange(""); setText(""); setOpen(false); }}
                className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-rose-600 px-2 py-1"
              >
                Clear
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Helpers ---------------------------------------------------------------
function isoToDMY(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

// Terima banyak format: "15/8/2025", "15-08-25", "15 8 2025", "15082025", "2025-08-15", dll.
function dmyToISO(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  // Coba dulu format ISO (yyyy-mm-dd atau yyyy/mm/dd)
  const iso = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(s);
  if (iso) {
    const yy = +iso[1], mm = +iso[2], dd = +iso[3];
    return validate(yy, mm, dd);
  }
  // dmy parts by separator
  const parts = s.split(/[\/\-.\s]+/).filter(Boolean);
  if (parts.length === 3) {
    const dd = +parts[0], mm = +parts[1];
    let yy = +parts[2];
    if (yy < 100) yy = yy + 2000; // "25" → 2025
    return validate(yy, mm, dd);
  }
  // No separator, pure digits
  const digits = s.replace(/\D/g, "");
  if (digits.length === 8) {
    const dd = +digits.slice(0, 2), mm = +digits.slice(2, 4), yy = +digits.slice(4, 8);
    return validate(yy, mm, dd);
  }
  if (digits.length === 6) {
    const dd = +digits.slice(0, 2), mm = +digits.slice(2, 4);
    const yy = 2000 + +digits.slice(4, 6);
    return validate(yy, mm, dd);
  }
  return "";
}

function validate(yy, mm, dd) {
  if (!dd || !mm || !yy) return "";
  if (mm < 1 || mm > 12) return "";
  if (dd < 1 || dd > 31) return "";
  const d = new Date(yy, mm - 1, dd);
  if (d.getFullYear() !== yy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${yy}-${pad(mm)}-${pad(dd)}`;
}
